import { useState } from "react";
import { MERGE_FIELDS, getMergeFieldCategories } from "@/lib/mergeFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ClauseMergeFieldPickerProps {
  onInsert: (field: string) => void;
}

export function ClauseMergeFieldPicker({ onInsert }: ClauseMergeFieldPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = getMergeFieldCategories();

  const filtered = MERGE_FIELDS.filter((f) => {
    const matchesSearch = !search ||
      f.label.toLowerCase().includes(search.toLowerCase()) ||
      f.key.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !selectedCategory || f.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleInsert = (key: string) => {
    onInsert(`{{${key}}}`);
    setOpen(false);
    setSearch("");
    setSelectedCategory(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <ChevronRight className="h-3.5 w-3.5 mr-1" />
          Insert Merge Field
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-3 border-b">
          <div className="text-sm font-semibold mb-2">Merge Fields</div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search fields..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-7 text-xs"
            />
          </div>
          {!search && (
            <div className="flex flex-wrap gap-1 mt-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                  className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                    selectedCategory === cat
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-muted text-muted-foreground border-border hover:border-primary/50"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-4">No fields match</div>
          ) : (
            filtered.map((field) => (
              <button
                key={field.key}
                onClick={() => handleInsert(field.key)}
                className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs font-medium truncate">{field.label}</div>
                    <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{`{{${field.key}}}`}</div>
                  </div>
                  <div className="text-[10px] text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">
                    e.g. {field.example}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
        <div className="p-2 border-t bg-muted/30">
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            Merge fields are replaced with live contract values when rendered.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
